import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import SwiperCore from 'swiper';
import { FreeMode, Autoplay, Navigation } from 'swiper/modules';
import CallbackPopup from './CallbackPopup';

SwiperCore.use([FreeMode, Autoplay, Navigation]);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const TestimonialsCarousel = ({ title = "Real Stories, Real Impact", limit = 6 }) => {
  const navigate = useNavigate();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const swiperRef = useRef(null);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/testimonials?featured=true&limit=${limit}&status=published`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch testimonials');
      }
      
      const data = await response.json();
      setTestimonials(data.testimonials || []);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      // Fallback to hardcoded testimonials if API fails
      setTestimonials([
        {
          id: 'fallback-1',
          name: 'Ahmed Zia',
          avatar: '/images/image copy 64.png',
          platform: 'Google',
          rating: 5,
          content: 'Medical Linkers helped me find the perfect treatment in Turkey. The entire process was seamless and professional.',
          displayDate: '2 weeks ago'
        },
        {
          id: 'fallback-2',
          name: 'Sarah Johnson',
          avatar: '/images/image copy 63.png',
          platform: 'Google',
          rating: 5,
          content: 'Outstanding service! They connected me with excellent doctors and made my medical journey stress-free.',
          displayDate: '1 month ago'
        },
        {
          id: 'fallback-3',
          name: 'Usman',
          avatar: '/images/image copy 62.png',
          platform: 'Website',
          rating: 5,
          content: 'Thanks to Medical Linkers and Miss Sonam, my mother got the best treatment in India. Truly grateful!',
          displayDate: '3 weeks ago'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const renderStars = (rating) => {
    return '★'.repeat(rating);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getPlatformLogo = (testimonial) => {
    if (testimonial.platformLogo) {
      return testimonial.platformLogo;
    }
    
    // Default platform logos
    switch (testimonial.platform) {
      case 'Google':
        return '/images/google-logo.png';
      case 'Facebook':
        return 'https://upload.wikimedia.org/wikipedia/commons/5/51/Facebook_f_logo_%282019%29.svg';
      case 'Trustpilot':
        return 'https://upload.wikimedia.org/wikipedia/commons/4/48/Trustpilot_logo.svg';
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-center mb-12">
          <span className="text-[#1976f2]">{title.split(' ')[0]} {title.split(' ')[1]}</span> , {title.split(' ').slice(2).join(' ')}
        </h2>
        <div className="flex justify-center items-center">
          <div className="text-gray-500">Loading testimonials...</div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section className="container mx-auto px-4 py-16">
      <h2 className="text-4xl font-bold text-center mb-12">
        <span className="text-[#1976f2]">Real Stories</span> , Real Impact
      </h2>
      
      {/* Google Logo */}
      <div className="object-cover justify-center mb-8">
        <img 
          src="/images/google.png" 
          alt="Google" 
          className="w-full"
        />
      </div>
      
      <div className="overflow-hidden relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={16}
          loop={true}
          freeMode={true}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
          speed={1000}
          grabCursor={true}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="w-full"
          >
          {[...testimonials, ...testimonials].map((testimonial, index) => (
            <SwiperSlide key={`${testimonial.id || testimonial._id || index}-${index}`}>
              <div className="transition-transform duration-300 ease-in-out transform hover:scale-105">
                <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300 h-full">
                {/* Platform and Rating Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    {getPlatformLogo(testimonial) && (
                      <img 
                        src={getPlatformLogo(testimonial)} 
                        alt={testimonial.platform} 
                        className="h-6 w-6 mr-2" 
                      />
                    )}
                    <span className="font-semibold text-gray-800">
                      {testimonial.title || testimonial.platform}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-yellow-400 text-lg">
                      {renderStars(testimonial.rating)}
                    </span>
                  </div>
                </div>

                {/* Testimonial Content */}
                <div className="mb-6">
                  <p className="text-gray-700 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                </div>

                {/* Author Information */}
                <div className="flex items-center">
                  <img 
                    src={testimonial.avatar} 
                    alt={testimonial.name} 
                    className="w-12 h-12 rounded-full object-cover mr-3 border-2 border-gray-200" 
                  />
                  <div>
                    <div className="flex items-center">
                      {testimonial.profileLink ? (
                        <a 
                          href={testimonial.profileLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#1976f2] font-semibold hover:underline"
                        >
                          {testimonial.name}
                        </a>
                      ) : (
                        <span className="text-[#1976f2] font-semibold">{testimonial.name}</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {formatDate(testimonial.displayDate)}
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </SwiperSlide>
            ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        <div className="flex justify-center gap-4 mt-10">
            <button 
            onClick={() => swiperRef.current?.swiper.slidePrev()}
            aria-label="Previous"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button 
            onClick={() => swiperRef.current?.swiper.slideNext()}
            aria-label="Next"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] hover:bg-blue-700 text-white transition-colors shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
      </div>

      {/* Call to Action Buttons */}
      <div className="flex justify-center gap-4 mt-12">
        <button 
          onClick={() => setIsCallbackOpen(true)}
          className="bg-[#1976f2] text-white px-8 py-3 rounded-full text-lg font-medium hover:bg-blue-600 transition shadow-lg"
        >
          Get A Call Back
        </button>
        <button 
          onClick={() => window.open('https://api.whatsapp.com/send/?phone=9599222655&text=Hello%2C+please+contact+me+regarding+https%3A%2F%2Fwww.medicallinkers.org%2F+%2C+Thank+you%21&type=phone_number&app_absent=0', '_blank')}
          className="border border-[#1976f2] text-[#1976f2] px-8 py-3 rounded-full text-lg font-medium hover:bg-blue-50 transition shadow-lg flex items-center gap-2"
        >
          <i className="ri-whatsapp-line"></i> Chat Now
        </button>
      </div>

      {/* Callback Popup */}
      <CallbackPopup 
        isOpen={isCallbackOpen}
        onClose={() => setIsCallbackOpen(false)}
      />
    </section>
  );
};

export default TestimonialsCarousel;
